import styled from '@emotion/styled';
import { DSButton, DSColumn, DSGrid, DSRow } from '@hundred5/design-system';
import { Formik } from 'formik';
import React from 'react';
import * as yup from 'yup';

import { FormikInputField, Loader } from '@/features/common';
import { EmailDownloadModal } from '@/features/email';
import { useAddCandidateMutation } from '@/features/job-opening';
import { useNotifications } from '@/features/notifications';
import { useWorkspaceStatsQuery } from '@/features/workspace';
import { useHistory, useWorkspaceId } from '@/hooks/router';

import { useShareModal } from '../hooks';

interface ICandidateFieldsFormValues {
  firstName: string;
  lastName: string;
  email: string;
}

export const AddCandidateForm = () => {
  const { jobOpeningIdSelected, categorySelected, closeShareModal } =
    useShareModal();

  const workspaceId = useWorkspaceId();
  const history = useHistory();
  const { data: workspaceStats } = useWorkspaceStatsQuery({ workspaceId });

  const addCandidateMutation = useAddCandidateMutation();
  const { addNotification } = useNotifications();

  const handleSubmit = async (values: ICandidateFieldsFormValues) => {
    if (categorySelected?.id && jobOpeningIdSelected) {
      addCandidateMutation.mutate(
        {
          jobOpeningId: jobOpeningIdSelected,
          attributes: {
            categoryId: categorySelected.id,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
          },
        },
        {
          onSuccess: (candidate) => {
            addNotification({ type: 'candidate_added' });
            closeShareModal();
            history.push(
              `/admin/${workspaceId}/openings/${jobOpeningIdSelected}/pipeline/stage/${categorySelected.id}/candidate/${candidate.id}/results`
            );
          },
        }
      );
    }
  };

  const canUnlockCandidate =
    workspaceStats && workspaceStats.unlockableTestTakersRemaining > 0;

  const submitDisabled =
    !jobOpeningIdSelected || !categorySelected || !canUnlockCandidate;

  return (
    <>
      <EmailFormWrapper>
        {jobOpeningIdSelected && !categorySelected ? (
          <Loader />
        ) : (
          <Formik<ICandidateFieldsFormValues>
            initialValues={{
              email: '',
              firstName: '',
              lastName: '',
            }}
            onSubmit={handleSubmit}
            validationSchema={schema}
            validateOnMount
            enableReinitialize
          >
            {({ submitForm, isValid, dirty }) => (
              <>
                <DSGrid gap="8px">
                  <GridTitle>Candidate information</GridTitle>
                  <DSRow gap="12px" smWrap>
                    <DSColumn size={12} sm={24}>
                      <FormikInputField
                        name="firstName"
                        id="add-candidate-first-name"
                        label="First Name"
                        required
                      />
                    </DSColumn>
                    <DSColumn size={12} sm={24}>
                      <FormikInputField
                        name="lastName"
                        id="add-candidate-last-name"
                        label="Last Name"
                        required
                      />
                    </DSColumn>
                  </DSRow>
                  <DSRow smWrap style={{ width: 'calc(50% - 6px)' }}>
                    <DSColumn style={{ width: '100%' }}>
                      <FormikInputField
                        name="email"
                        id="add-candidate-email"
                        label="Email"
                        required
                      />
                    </DSColumn>
                  </DSRow>
                </DSGrid>

                <FooterButtonWrapper>
                  <AddCandidateButton
                    variant="primary"
                    type="submit"
                    onClick={submitForm}
                    disabled={
                      !dirty ||
                      submitDisabled ||
                      !isValid ||
                      addCandidateMutation.isPending
                    }
                    {...(!canUnlockCandidate && {
                      'data-rh':
                        'You have reached the candidate limit. Upgrade to add more candidates.',
                      'data-rh-at': 'top',
                    })}
                  >
                    Add candidate
                  </AddCandidateButton>
                </FooterButtonWrapper>
              </>
            )}
          </Formik>
        )}
      </EmailFormWrapper>
      <EmailDownloadModal />
    </>
  );
};

const schema = yup.object().shape({
  email: yup
    .string()
    .required("Enter candidate's email")
    .email('Enter valid email'),
  firstName: yup.string().required("Enter candidate's first name"),
  lastName: yup.string().required("Enter candidate's last name"),
});

const EmailFormWrapper = styled.div`
  padding: 16px 36px 36px 36px;

  @media all and (max-width: 768px) {
    padding: 16px;
  }
`;

const FooterButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;

  @media all and (max-width: 768px) {
    position: relative;
    bottom: 0;
    margin-left: 0px;
    margin-right: 0px;
    padding: 0;
    flex-direction: column;
  }
`;

const AddCandidateButton = styled(DSButton)`
  @media all and (max-width: 768px) {
    width: 100%;
    border-radius: 8px;
  }
`;

const GridTitle = styled.div`
  font-weight: 500;
`;
